// src/pages/Contact/ContactPage.tsx
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { motion } from "framer-motion";
import HeroSection from "@/components/Marketing/HeroSection";

const schema = z.object({
  name: z.string().min(2, "Name is required"),
  email: z.string().email("Valid email required"),
  message: z.string().min(10, "Message too short"),
});

type ContactForm = z.infer<typeof schema>;

export default function ContactPage() {
  const form = useForm<ContactForm>({
    resolver: zodResolver(schema),
    defaultValues: { name: "", email: "", message: "" },
  });

  const onSubmit = async (values: ContactForm) => {
    console.log("Contact form submitted:", values);
    // In future: POST to /api/system/contact or /api/support
  };

  return (
    <div className="space-y-10">
      <HeroSection
        title="Contact IDSCRM"
        subtitle="Let’s build the future of dealership technology — together."
      />

      <section className="container mx-auto px-6 pb-20 grid md:grid-cols-2 gap-10">
        {/* Left: Contact form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card className="rounded-2xl shadow-lg">
            <CardHeader>
              <CardTitle>Send Us a Message</CardTitle>
            </CardHeader>
            <CardContent>
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Name</FormLabel>
                        <FormControl>
                          <Input placeholder="Your name" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email</FormLabel>
                        <FormControl>
                          <Input type="email" placeholder="your@email.com" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="message"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Message</FormLabel>
                        <FormControl>
                          <Textarea
                            rows={5}
                            placeholder="Tell us about your dealership, integration, or support need..."
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Button type="submit" className="w-full">
                    Send Message
                  </Button>
                </form>
              </Form>
            </CardContent>
          </Card>
        </motion.div>

        {/* Right: Info block */}
              <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="space-y-6"
              >
                  <h2 className="text-2xl font-semibold">Get in Touch</h2>
                  <p className="text-muted-foreground">
                      IDSCRM operates with a distributed team between the U.S. and Africa —
                      connecting dealerships, finance partners, and buyers across markets.
                      Our core development and operations run from Florida and Nairobi,
                      serving dealers in North America, the Caribbean, and East Africa.
                  </p>

                  <div className="space-y-2 text-sm">
                      <p>
                          <strong>Email:</strong> support@idscrm.com
                      </p>
                      <p>
                          <strong>WhatsApp / Call:</strong> +1 (954) 555-4310 (U.S.) &nbsp;|&nbsp; +254 (0)700-000-000 (Kenya)
                      </p>
                      <p>
                          <strong>Business Hours:</strong> Mon–Sat, 9 AM – 6 PM (local time)
                      </p>
                  </div>

                  <Card className="p-4 rounded-xl bg-muted/40 border-dashed">
                      <p className="text-sm text-muted-foreground">
                          <strong>Note:</strong> IDSCRM is a growing international platform — we currently
                          operate remotely with verified on-ground partners in select regions.
                          Meetings are available online or by appointment.
                      </p>
                  </Card>
              </motion.div>

      </section>
    </div>
  );
}
